import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend
} from 'chart.js';
import { FiChevronLeft, FiChevronRight, FiArrowUp, FiArrowDown, FiMinus } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import DashboardAcessos from './DashboardAcessos';
import '../styles/Dashboard.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Legend);

export const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

export const mesCorrente = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export const deslocarMes = (mes, passo) => {
  const [a, m] = mes.split('-').map(Number);
  const d = new Date(a, m - 1 + passo, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export const rotuloMes = (mes) => {
  if (!mes) return '';
  const [a, m] = mes.split('-').map(Number);
  return `${MESES[m - 1]} de ${a}`;
};

const moeda = (v) => (v === null || v === undefined)
  ? '—'
  : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const inteiro = (v) => (v === null || v === undefined) ? '—' : v.toLocaleString('pt-BR');

/** 200 minutos lidos como "3h20" dizem mais do que "200min". */
const duracao = (min) => {
  if (min === null || min === undefined) return '—';
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  if (h === 0) return `${m}min`;
  return m === 0 ? `${h}h` : `${h}h ${String(m).padStart(2, '0')}min`;
};

/**
 * A variação sozinha não diz se a notícia é boa: mais entradas é bom, mais
 * tempo parado no pátio não necessariamente. `polaridade` separa os dois casos;
 * 'neutra' mostra a seta sem pintar de verde ou vermelho.
 */
export const Variacao = ({ pct, polaridade = 'positiva' }) => {
  if (pct === null || pct === undefined || !isFinite(pct)) {
    return <span className="kpi-delta neutro"><FiMinus /> sem base</span>;
  }
  const arredondado = Math.round(pct * 10) / 10;
  if (Math.abs(arredondado) < 0.05) {
    return <span className="kpi-delta neutro"><FiMinus /> estável</span>;
  }
  const subiu = arredondado > 0;
  const classe = polaridade === 'neutra' ? 'neutro' : ((subiu === (polaridade === 'positiva')) ? 'bom' : 'ruim');
  return (
    <span className={`kpi-delta ${classe}`}>
      {subiu ? <FiArrowUp /> : <FiArrowDown />}
      {Math.abs(arredondado).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%
    </span>
  );
};

export const Kpi = ({ rotulo, valor, indicador, formatar, polaridade, nota }) => (
  <div className="kpi">
    <span className="kpi-label">{rotulo}</span>
    <strong className="kpi-value">{valor}</strong>
    <div className="kpi-foot">
      <Variacao pct={indicador?.delta_pct} polaridade={polaridade} />
      <span className="kpi-prev">antes {formatar(indicador?.previous)}</span>
    </div>
    {nota && <span className="kpi-note">{nota}</span>}
  </div>
);

const Dashboard = () => {
  // Onda 55: onde não há ticket, o painel de tickets não mede nada — no Ipê ele
  // mostrava zero em tudo com dezenas de reconhecimentos no banco. As verticais
  // que registram acesso têm o painel próprio, em DashboardAcessos. O painel de
  // estacionamento abaixo segue exatamente como estava.
  const { vertical } = useAuth();
  if (vertical === 'clube_social' || vertical === 'condominio') return <DashboardAcessos />;
  return <DashboardEstacionamento />;
};

const DashboardEstacionamento = () => {
  const { can } = useAuth();
  const [mes, setMes] = useState(mesCorrente);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);

  const buscar = useCallback(async (mesAlvo) => {
    setCarregando(true);
    try {
      const r = await api.get('/api/dashboard/overview', { params: { month: mesAlvo } });
      setDados(r.data);
      setErro(null);
    } catch (e) {
      // O painel antigo engolia o erro no console e ficava com os cartões
      // zerados, o que se confunde com um dia sem movimento.
      setErro(e.response?.data?.error || 'Não foi possível carregar os indicadores.');
      setDados(null);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { buscar(mes); }, [mes, buscar]);

  // Só o mês corrente se atualiza sozinho: um mês fechado não muda mais.
  useEffect(() => {
    if (mes !== mesCorrente()) return undefined;
    const t = setInterval(() => buscar(mes), 60000);
    return () => clearInterval(t);
  }, [mes, buscar]);

  const anos = useMemo(() => {
    const atual = new Date().getFullYear();
    return [atual, atual - 1, atual - 2];
  }, []);

  const [anoSel, mesSel] = mes.split('-').map(Number);
  const ehMesCorrente = mes === mesCorrente();

  const graficoDiario = useMemo(() => ({
    labels: (dados?.daily || []).map(d => String(d.day)),
    datasets: [
      {
        label: 'Entradas',
        data: (dados?.daily || []).map(d => d.entries),
        backgroundColor: '#14657a',
        borderRadius: 3,
      },
      {
        label: 'Saídas',
        data: (dados?.daily || []).map(d => d.exits),
        backgroundColor: '#8fbecb',
        borderRadius: 3,
      },
    ],
  }), [dados]);

  const graficoHorario = useMemo(() => ({
    labels: (dados?.hourly || []).map(h => `${String(h.hour).padStart(2, '0')}h`),
    datasets: [{
      label: 'Entradas',
      data: (dados?.hourly || []).map(h => h.entries),
      backgroundColor: '#14657a',
      borderRadius: 3,
    }],
  }), [dados]);

  const opcoes = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: 'bottom', labels: { boxWidth: 10, boxHeight: 10, font: { size: 11 }, color: '#5b7280' } },
      tooltip: { backgroundColor: '#10202a', padding: 10, cornerRadius: 6, titleFont: { size: 12 }, bodyFont: { size: 12 } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: '#7d939f', font: { size: 11 } } },
      y: { beginAtZero: true, grid: { color: '#ebf0f3' }, ticks: { color: '#7d939f', font: { size: 11 }, precision: 0 } },
    },
  };

  const opcoesHorario = { ...opcoes, plugins: { ...opcoes.plugins, legend: { display: false } } };

  const k = dados?.kpis;
  const taxaSaida = (dados && k && k.entries.value > 0)
    ? (k.exits.value / k.entries.value) * 100
    : null;

  const picoHora = useMemo(() => {
    if (!dados?.hourly?.length) return null;
    return dados.hourly.reduce((a, b) => (b.entries > a.entries ? b : a));
  }, [dados]);

  return (
    <div className="dashboard">
      <div className="page-header">
        <div>
          <h1>Dashboard</h1>
          <p className="page-subtitle">
            Indicadores de {rotuloMes(mes)}, comparados com {rotuloMes(dados?.period?.previous_month || deslocarMes(mes, -1))}.
          </p>
        </div>

        <div className="periodo">
          <button className="btn btn-secondary btn-sm" onClick={() => setMes(deslocarMes(mes, -1))} title="Mês anterior">
            <FiChevronLeft />
          </button>

          <select value={mesSel} onChange={(e) => setMes(`${anoSel}-${String(Number(e.target.value)).padStart(2, '0')}`)} aria-label="Mês">
            {MESES.map((nome, i) => <option key={nome} value={i + 1}>{nome}</option>)}
          </select>

          <select value={anoSel} onChange={(e) => setMes(`${e.target.value}-${String(mesSel).padStart(2, '0')}`)} aria-label="Ano">
            {anos.map(a => <option key={a} value={a}>{a}</option>)}
          </select>

          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setMes(deslocarMes(mes, 1))}
            disabled={ehMesCorrente}
            title={ehMesCorrente ? 'Este já é o mês corrente' : 'Mês seguinte'}
          >
            <FiChevronRight />
          </button>

          {!ehMesCorrente && (
            <button className="btn btn-secondary btn-sm" onClick={() => setMes(mesCorrente())}>Mês atual</button>
          )}
        </div>
      </div>

      <div className="patio-agora">
        <span className="patio-label">No pátio agora</span>
        <strong className="patio-valor">{inteiro(dados?.current_vehicles)}</strong>
        <span className="patio-nota">veículos com ticket em aberto — número do momento, não do mês</span>
        {/* Onda 158: o total aberto pela carência da Reconciliação, para os dois números conversarem. */}
        {dados?.current_vehicles_split && (
          <span className="patio-divisao">
            <span><b>{inteiro(dados.current_vehicles_split.recent)}</b> entraram nas últimas {dados.current_vehicles_split.grace_hours} h</span>
            <span className="patio-divisao-antigos">
              <b>{inteiro(dados.current_vehicles_split.older)}</b> abertos há mais de {dados.current_vehicles_split.grace_hours} h, sem saída lida
              {dados.current_vehicles_split.older > 0 && can && can('reconciliation') && (
                <> · <Link to="/reconciliation">ver na Reconciliação</Link></>
              )}
            </span>
          </span>
        )}
      </div>

      {erro && <div className="painel-erro">{erro}</div>}

      {carregando && !dados ? (
        <div className="loading"><div className="spinner" /><p>Carregando indicadores...</p></div>
      ) : dados && (
        <>
          <div className="kpis">
            <Kpi rotulo="Entradas" valor={inteiro(k.entries.value)} indicador={k.entries} formatar={inteiro} />
            <Kpi
              rotulo="Saídas registradas"
              valor={inteiro(k.exits.value)}
              indicador={k.exits}
              formatar={inteiro}
              nota={taxaSaida === null ? null : `${taxaSaida.toFixed(0)}% das entradas tiveram saída registrada`}
            />
            <Kpi
              rotulo="Permanência média"
              valor={duracao(k.avg_stay_minutes.value)}
              indicador={k.avg_stay_minutes}
              formatar={duracao}
              polaridade="neutra"
              nota={dados.stay_excluded > 0
                ? `${inteiro(dados.stay_sample)} estadias; ${inteiro(dados.stay_excluded)} acima de ${dados.abandon_window_hours}h ficaram de fora`
                : `${inteiro(dados.stay_sample)} estadias`}
            />
            <Kpi rotulo="Receita" valor={moeda(k.revenue.value)} indicador={k.revenue} formatar={moeda} />
            <Kpi rotulo="Ticket médio" valor={moeda(k.avg_ticket.value)} indicador={k.avg_ticket} formatar={moeda} />
            <Kpi rotulo="Tickets pagos" valor={inteiro(k.paid_tickets.value)} indicador={k.paid_tickets} formatar={inteiro} />
          </div>

          <div className="graficos">
            <section className="grafico">
              <header>
                <h2>Movimento por dia</h2>
                <span>{rotuloMes(mes)}</span>
              </header>
              <div className="grafico-area"><Bar data={graficoDiario} options={opcoes} /></div>
            </section>

            <section className="grafico">
              <header>
                <h2>Entradas por hora do dia</h2>
                {picoHora && picoHora.entries > 0 && (
                  <span>pico às {String(picoHora.hour).padStart(2, '0')}h, {inteiro(picoHora.entries)} entradas</span>
                )}
              </header>
              <div className="grafico-area"><Bar data={graficoHorario} options={opcoesHorario} /></div>
            </section>
          </div>

          <section className="top-placas">
            <header><h2>Veículos mais frequentes no mês</h2></header>
            {dados.top_plates.length === 0 ? (
              <p className="no-data">Nenhuma entrada registrada neste mês</p>
            ) : (
              <ol>
                {dados.top_plates.map((p, i) => (
                  <li key={p.plate}>
                    <span className="pos">{i + 1}</span>
                    <span className="placa">{p.plate}</span>
                    <span className="barra" aria-hidden="true">
                      <span style={{ width: `${(p.visits / dados.top_plates[0].visits) * 100}%` }} />
                    </span>
                    <span className="visitas">{inteiro(p.visits)}</span>
                  </li>
                ))}
              </ol>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default Dashboard;
